import React from 'react';
import { Button } from './ui/button';
import { Copy, ExternalLink, MessageCircle, Share2 } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const SocialShareButtons = ({ profileUrl, agentName, slug }) => {
  const shareUrl = `${API_URL}/api/agent/share/${slug}`;
  const shareText = `Check out ${agentName}'s property listings on Shopperden — 3D virtual tours, AI valuations & more!`;
  const encodedUrl = encodeURIComponent(shareUrl);
  const encodedText = encodeURIComponent(shareText);

  const copyLink = () => {
    navigator.clipboard.writeText(shareUrl);
    toast.success('Share link copied! Paste it anywhere.');
  };

  return (
    <div className="space-y-4" data-testid="social-share-section">
      <h4 className="text-sm font-semibold text-[#0F172A] flex items-center gap-2">
        <Share2 className="h-4 w-4 text-[#E07A3A]" />
        Share on Social Media
      </h4>
      <p className="text-xs text-slate-500">
        Share your profile link — it shows a professional preview card with your name, role, and Shopperden branding on WhatsApp, Facebook & Messenger.
      </p>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {/* WhatsApp */}
        <a
          href={`https://wa.me/?text=${encodedText}%20${encodedUrl}`}
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center justify-center gap-2 bg-[#25D366] text-white px-4 py-3 text-xs font-semibold tracking-wider uppercase hover:bg-[#1da851] transition-colors"
          data-testid="share-whatsapp-btn"
        >
          <svg className="h-4 w-4 fill-current" viewBox="0 0 24 24"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
          WhatsApp
        </a>

        {/* Facebook */}
        <a
          href={`https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}&quote=${encodedText}`}
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center justify-center gap-2 bg-[#1877F2] text-white px-4 py-3 text-xs font-semibold tracking-wider uppercase hover:bg-[#1565c0] transition-colors"
          data-testid="share-facebook-btn"
        >
          <svg className="h-4 w-4 fill-current" viewBox="0 0 24 24"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>
          Facebook
        </a>

        {/* Messenger */}
        <a
          href={`https://www.facebook.com/dialog/send?link=${encodedUrl}&app_id=0&redirect_uri=${encodedUrl}`}
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center justify-center gap-2 bg-gradient-to-r from-[#0695FF] to-[#A334FA] text-white px-4 py-3 text-xs font-semibold tracking-wider uppercase hover:opacity-90 transition-opacity"
          data-testid="share-messenger-btn"
        >
          <MessageCircle className="h-4 w-4" />
          Messenger
        </a>

        {/* Copy Link */}
        <Button
          onClick={copyLink}
          className="flex items-center justify-center gap-2 bg-[#0F172A] text-white px-4 py-3 text-xs font-semibold tracking-wider uppercase hover:bg-[#1e293b]"
          data-testid="share-copy-btn"
        >
          <Copy className="h-4 w-4" />
          Copy Link
        </Button>
      </div>

      {/* Preview info */}
      <div className="bg-slate-50 border border-slate-100 p-4 flex items-start gap-3">
        <div className="w-10 h-10 bg-[#0F172A] flex items-center justify-center flex-shrink-0">
          <span className="text-[#F0A875] font-semibold text-sm">S</span>
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-xs text-slate-400 mb-1">Preview — how your link looks when shared</p>
          <p className="text-sm font-semibold text-[#0F172A] truncate">{agentName} on Shopperden</p>
          <p className="text-xs text-slate-500 truncate">Browse property listings, 3D virtual tours & AI valuations</p>
          <p className="text-[10px] text-[#E07A3A] mt-1 truncate">{shareUrl}</p>
        </div>
      </div>

      <p className="text-[10px] text-slate-400 leading-relaxed">
        When you paste this link on WhatsApp, Facebook, or Messenger, it will automatically show a rich preview card with your name, role and Shopperden branding. Your customers click the link and land directly on your Shopperden profile.
      </p>
    </div>
  );
};

export default SocialShareButtons;
